import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { FiCalendar, FiCheck, FiPlus } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const YearSelector = () => {
  const { years, selectedYear, currentYear, changeYear, createYear, activateYear } = useAcademicYear();
  const { hasPermission } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newYear, setNewYear] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);

  const handleCreateYear = async (e) => {
    e.preventDefault();
    if (newYear.match(/^\d{4}-\d{4}$/)) {
      const result = await createYear({ annee_scolaire: newYear });
      if (result.success) {
        setShowCreateModal(false);
        setNewYear('');
      }
    }
  };

  const handleActivateYear = async (yearId) => {
    if (window.confirm('Voulez-vous activer cette année scolaire ?')) {
      await activateYear(yearId);
    }
  };

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [showDropdown]);

  if (!selectedYear) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <FiCalendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {selectedYear.annee_scolaire}
        </span>
        {selectedYear.id === currentYear?.id && (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
            Active
          </span>
        )}
      </button>

      {/* Dropdown Portal */}
      {showDropdown && ReactDOM.createPortal(
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setShowDropdown(false)}
          ></div>
          <div 
            className="fixed w-64 bg-white rounded-lg shadow-lg border border-gray-200"
            style={{ 
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              zIndex: 9999
            }}
          >
            <div className="p-2 max-h-96 overflow-y-auto">
              <div className="text-xs font-medium text-gray-500 uppercase px-3 py-2">
                Années scolaires
              </div>
              
              {years.map((year) => (
                <div
                  key={year.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded"
                >
                  <button
                    onClick={() => {
                      changeYear(year.id);
                      setShowDropdown(false);
                    }}
                    className="flex items-center space-x-2 flex-1 text-left"
                  >
                    <span className={`text-sm ${year.id === selectedYear.id ? 'font-medium text-primary-600' : 'text-gray-700'}`}>
                      {year.annee_scolaire}
                    </span>
                    {year.id === selectedYear.id && (
                      <FiCheck className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                  
                  {hasPermission('settings.update') && year.id !== currentYear?.id && (
                    <button
                      onClick={() => handleActivateYear(year.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 px-2"
                    >
                      Activer
                    </button>
                  )}
                  
                  {year.id === currentYear?.id && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      Active
                    </span>
                  )}
                </div>
              ))}
              
              {hasPermission('settings.update') && (
                <div className="border-t mt-2 pt-2">
                  <button
                    onClick={() => {
                      setShowCreateModal(true);
                      setShowDropdown(false);
                    }}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded"
                  >
                    <FiPlus className="h-4 w-4" />
                    <span>Nouvelle année scolaire</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Create Year Modal Portal */}
      {showCreateModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Nouvelle année scolaire
            </h3>
            
            <form onSubmit={handleCreateYear}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Année scolaire
                </label>
                <input
                  type="text"
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  placeholder="2024-2025"
                  pattern="\d{4}-\d{4}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Format: YYYY-YYYY (ex: 2024-2025)
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewYear('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default YearSelector;